import React, {
  ComponentType,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  Button,
  Form,
  Grid,
  Message
} from 'semantic-ui-react'
import { DateInput } from 'semantic-ui-calendar-react'
import RichTextEditor, {
  EditorValue, ToolbarConfig
} from 'react-rte'
import filter from 'lodash/filter'
import {
  parse,
  format
} from 'date-fns'
// Local
import {
  CONDITION,
  FORMAT,
  shouldRenderReskin2025
} from '../../../constant'
import { isSameYear } from '../../common/dateTimeUtils'
import {
  TITLE_ERROR,
  toolbarConfig
} from '../condition/common'
import ReasonHandler, {
  NEW_RULE
} from '../condition/reason'
import Icd10Search from '../condition/Icd10Search'
import If from '../../common/if'
import ReveleerButton from '../../common/ReveleerButton'
import {
  EncounterConditionDisplay,
  EncounterConditionRuleRow,
  User
} from '../../authenticated/types'
import { RootState } from '../../../store'

const SafeForm = Form as unknown as ComponentType<any>
const SafeMessage = Message as unknown as ComponentType<any>
const SafeDateInput = DateInput as unknown as ComponentType<any>

type Rule = {
  id?: string | number | undefined
  ruleType?: string
  type?: string | undefined
  title?: string
  statusCode?: string | keyof typeof CONDITION.STATUS
  reason: string
  encounterConditionId?: string
  targetType?: string | null
  targetId?: string |  undefined
}

type ErrorState = {
  description: string
  field: string
} | null

type CdiConditionEditorOwnProps = {
  condition: EncounterConditionDisplay
  encounterDate: number
  onCancel: () => void
  onSave: (condition: EncounterConditionDisplay) => void
}

type CdiConditionEditorReduxProps = {
  currentUser: User
}

type CdiConditionEditorProps =
  CdiConditionEditorOwnProps &
  CdiConditionEditorReduxProps

/**
 * Provides a condition editor for users with CDI role
 *
 * Note that saving the condition and the condition rules are two different
 * API requests. The save callback will need to handle this
 *
 * @param condition Condition entity
 * @param encounterDate Date of the encounter
 * @param onCancel Callback for cancel
 *        Arguments: None
 * @param onSave Callback for condition save button click
 *        Arguments: Condition, Rules
 */
function CdiConditionEditor(
  props: CdiConditionEditorProps
): JSX.Element {
  const {
    condition: conditionProp,
    encounterDate,
    onCancel,
    onSave,
    currentUser
  } = props

  const { cdiTags } = conditionProp

  const editorContent = RichTextEditor.createValueFromString(
    conditionProp.content || '',
    'markdown'
  )
  const icd10Code = conditionProp.icd10CodeJson && conditionProp.icd10CodeJson[0]
    ? conditionProp.icd10CodeJson[0]
    : ''
  const rules: Rule[] = conditionProp.rules
    ? conditionProp.rules.map((r: EncounterConditionRuleRow) => ({
      id: r.id,
      ruleType: r.ruleInputJson ? (r.ruleInputJson as any).ruleType : undefined,
      type: undefined,
      title: r.title,
      statusCode: r.statusCode,
      reason: r.reason || '',
      encounterConditionId: r.encounterConditionId
    }))
    : []
  const addressedDate = conditionProp.addressedDate
    ? format(conditionProp.addressedDate, FORMAT.DATE)
    : ''
  const hasModifiedAddressedDate: boolean = false
  const error = !conditionProp.title || conditionProp.title === ''
    ? {
      description: TITLE_ERROR,
      field: 'title'
    }
    : null

  const [ content, setContent ] = useState<EditorValue>(editorContent)
  const [ icd10CodeState, setIcd10CodeState ] = useState<string>(icd10Code)
  const [ title, setTitle ] = useState<string>(conditionProp.title)
  const [ rulesState, setRulesState ] = useState<Rule[]>(rules)
  const [ addressedDateState, setAddressedDateState ] = useState<string>(addressedDate)
  const [ hasModifiedAddressedDateState, setHasModifiedAddressedDateState ] = useState<boolean>(hasModifiedAddressedDate)
  const [ errorState, setErrorState ] = useState<ErrorState>(error)

  const onChangeValue = (
    event: React.SyntheticEvent,
    data: {
      name: string
      value: string | Rule[]
    }
  ) => {
    const { name, value } = data
    switch (name) {
      case 'title':
        setTitle(value as string)
        break
      case 'addressedDate':
        setAddressedDateState(value as string)
        setHasModifiedAddressedDateState(true)
        break
      case 'rules':
        setRulesState(value as Rule[])
        break
      default:
        break
    }
  }

  const onChangeContent = (newContent: EditorValue) => {
    setContent(newContent)
  }

  const onSelectIcd10 = (result: {
    id: string
    description: string
  }) => {
    setIcd10CodeState(result.id)
    setTitle(result.description)
  }

  const handleClickSave = () => {
    if (!title || title === '') {
      setErrorState({
        description: TITLE_ERROR,
        field: 'title'
      })
      return
    }

    const updatedCondition: EncounterConditionDisplay = {
      ...conditionProp,
      title,
      content: content.toString('markdown'),
      addressedDate: addressedDateState
        ? parse(addressedDateState, FORMAT.DATE, new Date())
        : null,
      icd10CodeJson: icd10CodeState && icd10CodeState.length > 0
        ? [ icd10CodeState ]
        : []
    }

    if ( updatedCondition.checkMark && hasModifiedAddressedDateState ) {
      // if the CDI changes the addressed date force an ra-eligibility
      // checkmark (checked or unchecked) on the condition
      if ( isSameYear(addressedDateState, encounterDate) ) {
        updatedCondition.checkMark = CONDITION.CHECKMARK.GREEN_CHECK;
      } else {
        updatedCondition.checkMark = CONDITION.CHECKMARK.UNCHECKED;
      }
    }

    const conditionRules: Partial<EncounterConditionRuleRow>[] = filter(
      rulesState,
      rule => {
        if (rule.type === NEW_RULE) {
          return !(rule.reason.length === 0 || !rule.reason.trim())
        }
        return true
      }
    ).map(rule => {
      const baseRule: Partial<EncounterConditionRuleRow> = {
        id: rule.id ? String(rule.id) : undefined,
        ruleInputJson: rule.ruleType ? { ruleType: rule.ruleType } as any : null,
        title: rule.title,
        reason: rule.reason,
        statusCode: rule.statusCode as string,
        encounterConditionId: conditionProp.id,
        targetType: conditionProp.rules && conditionProp.rules.length > 0 && conditionProp.rules[0].targetType ? conditionProp.rules[0].targetType : null,
        targetId: conditionProp.rules && conditionProp.rules.length > 0 && conditionProp.rules[0].targetId ? conditionProp.rules[0].targetId : null
      }
      
      if (rule.type === NEW_RULE) {
        const newRule: Partial<EncounterConditionRuleRow> = {
          ...baseRule,
          id: undefined,
          title: rule.reason,
          statusCode: CONDITION.STATUS.ACTIVE
        }
        return newRule
      }
      
      if (rule.reason.length === 0 || !rule.reason.trim()) {
        return {
          ...baseRule,
          statusCode: CONDITION.STATUS.REMOVED
        }
      }
      
      return baseRule
    })

    updatedCondition.rules = conditionRules as EncounterConditionRuleRow[]

    onSave(updatedCondition)
  }

  return (
    <Grid.Row className="first-row" key={ conditionProp.id }>
      <Grid.Column>
        <SafeForm error={ errorState !== null }>
          <If isTrue={ errorState !== null }>
            <SafeMessage error>
              { errorState && errorState.description }
            </SafeMessage>
          </If>
          <Form.Group widths="equal">
            <Form.Input
              error={ errorState !== null && errorState.field === 'title' }
              name="title"
              label="Condition Title"
              placeholder="Title"
              value={ title }
              onChange={ onChangeValue }
            />
            <div className="field">
              { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
              <label>ICD10 Code</label>
              <Icd10Search
                value={ icd10CodeState }
                onSelect={ onSelectIcd10 }
              />
            </div>
          </Form.Group>
          <div className="field">
            { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
            <label>Reasons</label>
            <div>
              { React.createElement(ReasonHandler as any, {
                rules: rulesState || [],
                onChangeValue
              }) }
            </div>
          </div>
          <div className="field">
            { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
            <label>Content</label>
            <div>
              <RichTextEditor
                value={ content }
                onChange={ onChangeContent }
                toolbarConfig={ toolbarConfig as ToolbarConfig }
              />
            </div>
          </div>
          <span className="field">
            <SafeDateInput
              name="addressedDate"
              label="Addressed Date"
              placeholder="Addressed Date"
              value={ addressedDateState }
              onChange={ onChangeValue }
              dateFormat={ FORMAT.DATE }
            />
          </span>
          <div className="m-1-top text-align-right">
            <If isTrue={ !shouldRenderReskin2025 }>
              <Button
                color="blue"
                id="cdi-condition-editor-update-button"
                type="button"
                onClick={ handleClickSave }
              >
                Update
              </Button>
              <Button
                id="cdi-condition-editor-cancel-button"
                type="button"
                onClick={ onCancel }
              >
                Cancel
              </Button>
            </If>
            <If isTrue={ shouldRenderReskin2025 }>
              <ReveleerButton
                buttonLabel="Update"
                buttonSize="small"
                buttonType="primary"
                id="cdi-condition-editor-update-button"
                isDisabled={ false }
                onClick={ handleClickSave }
                type="button"
              />
              <ReveleerButton
                buttonLabel="Cancel"
                buttonSize="small"
                buttonType="secondary"
                id="cdi-condition-editor-cancel-button"
                isDisabled={ false }
                onClick={ onCancel }
                type="button"
              />
            </If>
          </div>
        </SafeForm>
      </Grid.Column>
    </Grid.Row>
  )
}

const mapStateToProps = (state: RootState): CdiConditionEditorReduxProps => ({
  currentUser: state.user
})

export default connect(
  mapStateToProps,
  {}
)(CdiConditionEditor)
